import axios from 'axios'
import router from '../../router'
import { useUserStore } from '../../stores/user-store'

let isRedirecting = false

const api = axios.create({
  baseURL: import.meta.env.VITE_API_ENDPOINT,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    Accept: '*/*',
  },
})

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken')

    if (token) {
      config.headers.Authorization = `${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  },
)

// Rotas de autenticação/credencial: um 401 aqui NÃO deve disparar refresh nem
// "sessão expirada" — é senha/código/refresh inválido, tratado inline na própria tela.
// Inclui TODAS as rotas de 2FA (verify, setup, confirm e o disable, que responde 401
// "Senha incorreta"): sem isto, errar a senha ao desativar o 2FA deslogava o usuário.
const isAuthRoute = (url?: string) =>
  !!url &&
  (url.includes('/sign_in') ||
    url.includes('/login') ||
    url.includes('/two_factor') ||
    url.includes('/token/refresh'))

// Renovação silenciosa: troca o refresh token por um access novo. Usa o axios global
// (não a instância `api`) para não reentrar no interceptor. Dedupe via refreshPromise.
let isRefreshing = false
let refreshPromise: Promise<string | null> | null = null

// Resultado: token novo (sucesso); null = refresh inválido/revogado → deslogar;
// THROW = erro transitório (5xx/429/timeout/rede, ex.: durante um deploy/restart do
// Puma) → NÃO deslogar, manter os tokens e propagar o erro da request original.
async function requestRefresh(): Promise<string | null> {
  const refreshToken = localStorage.getItem('refreshToken')
  if (!refreshToken) return null

  try {
    const resp = await axios.post(
      `${import.meta.env.VITE_API_ENDPOINT}users/token/refresh`,
      { refresh_token: refreshToken },
      { headers: { 'Content-Type': 'application/json', Accept: 'application/json' } },
    )
    const newToken = resp.headers.authorization
    if (newToken) {
      localStorage.setItem('authToken', newToken)
      return newToken
    }
    return null
  } catch (err: any) {
    const st = err.response?.status
    // Só desloga quando o refresh é REALMENTE inválido/revogado (401/422).
    if (st === 401 || st === 422) return null
    // Transitório: não derruba a sessão — deixa o interceptor propagar o erro original.
    throw err
  }
}

function sessionExpired() {
  if (isRedirecting) return
  isRedirecting = true
  localStorage.removeItem('authToken')
  localStorage.removeItem('refreshToken')
  try {
    useUserStore().clearUserData()
  } catch (_) {}
  router.push({ name: 'session-expired' }).finally(() => {
    isRedirecting = false
  })
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config
    const status = error.response?.status

    if (status === 401 && original && !isAuthRoute(original.url)) {
      // já tentou renovar e ainda deu 401 → sessão realmente expirou/foi revogada
      if (original._retried) {
        sessionExpired()
        return Promise.reject(error)
      }
      original._retried = true

      if (!isRefreshing) {
        isRefreshing = true
        refreshPromise = requestRefresh().finally(() => {
          isRefreshing = false
        })
      }

      let newToken: string | null
      try {
        newToken = await refreshPromise
      } catch (_) {
        // Refresh falhou por erro transitório (deploy/5xx/rede): NÃO desloga, mantém
        // os tokens e propaga o erro original — a sessão sobrevive à janela de falha.
        return Promise.reject(error)
      }

      if (newToken) {
        return api(original) // reexecuta com o access novo (request interceptor injeta)
      }
      sessionExpired()
    }

    return Promise.reject(error)
  },
)

export default api
